import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Box, TextField, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import InputMask from "react-input-mask";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import { DEFAULT_WORKING_TIME } from "../../workingTimeConstants";
import {
  addAndEditStoreSchema,
  showCustomInputFieldError,
  validateCnpj,
  validatePhoneNumber,
} from "../storeValidationSchemas";
import ComercialInterfaceHeader from "../../../../../components/comercial/ComercialInterfaceHeader";
import GalleryImageList from "../../../../../components/comercial/ImageGallery";
import ImageGalleryHeader from "../../../../../components/comercial/ImageGallery/ImageGalleryHeader";
import WorkingScaleSelector from "../../../../../components/comercial/WorkingScaleSelector";
import { PrimaryButton } from "../../../../../components/general/buttons/PrimaryButton";
import { SecondaryButton } from "../../../../../components/general/buttons/SecondaryButton";
import storeApi from "../../../../../services/api/Store";
import ComercialDrawer from "../../../../../components/pet-beauty/CormercialDrawer";
import { IStore, UpdateStorePayload } from "../../../../../services/api/Store/types";
import { sharedStyles } from "../../../../../styles/shared";
import { getStoreImagesFirebasePath } from "../../../../../utils/stringUtils";
import { uploadOriginalAndPreviewImagesToFirebase } from "../../../../../utils/firebase/functions";
import ImageGalleryContainer from "../../../../../components/comercial/ImageGallery/ImageGalleryContainer";
import { LeftMarginBackButton } from "@/components/general/buttons/BackPageButton";
import CoordinateSelector from "@/components/general/CoordinateSelector";
import fetchCep from "@/hooks/useCep";
import { Trans, useTranslation } from "react-i18next";

export const DEFAULT_COORDINATES: [number, number] = [-1, -1];

const AddStore = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const storeNameRef = useRef<HTMLInputElement>(null);
  const [cnpj, setCnpj] = useState<string>("");
  const cnpjRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const [phone, setPhone] = useState<string>("");
  const phoneRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);

  const streetRef = useRef<HTMLInputElement>(null);
  const numberRef = useRef<HTMLInputElement>(null);
  const districtRef = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);
  const stateRef = useRef<HTMLInputElement>(null);

  const gatewayIdRef = useRef<HTMLInputElement>(null);
  const bankRef = useRef<HTMLInputElement>(null);
  const issuerRef = useRef<HTMLInputElement>(null);
  const issuerDigitRef = useRef<HTMLInputElement>(null);
  const accountNumberRef = useRef<HTMLInputElement>(null);
  const bankDigitRef = useRef<HTMLInputElement>(null);
  const pixKeyRef = useRef<HTMLInputElement>(null);

  const [accountType, setAccountType] = useState("CHECKING_ACCOUNT");
  const [transferType, setTransferType] = useState("PIX");
  const [pixKeyType, setPixKeyType] = useState("CPF");

  const [coordinates, setCoordinates] =
    useState<[number, number]>(DEFAULT_COORDINATES);

  const [cep, setCep] = useState<string>("");

  const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(false);

  const [images, setImages] = useState<HTMLCanvasElement[]>([]);

  const [intervalMinutes, setIntervalMinutes] = useState<number>(60);

  const [timeMonday, setTimeMonday] = useState<string[]>(DEFAULT_WORKING_TIME);
  const [timeTuesday, setTimeTuesday] =
    useState<string[]>(DEFAULT_WORKING_TIME);
  const [timeWednesday, setTimeWednesday] =
    useState<string[]>(DEFAULT_WORKING_TIME);
  const [timeThursday, setTimeThursday] =
    useState<string[]>(DEFAULT_WORKING_TIME);
  const [timeFriday, setTimeFriday] = useState<string[]>(DEFAULT_WORKING_TIME);
  const [timeSaturday, setTimeSaturday] = useState<string[]>([]);
  const [timeSunday, setTimeSunday] = useState<string[]>([]);

  const [submittedOnce, setSubmittedOnce] = useState<boolean>(false);

  const [generalErrorMessage, setGeneralErrorMessage] = useState("");

  const foundedCepAddress = fetchCep(
    cep,
    streetRef,
    districtRef,
    cityRef,
    stateRef,
    true
  );

  const {
    register,
    handleSubmit: onSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(addAndEditStoreSchema) });

  const handleStoreCreation = async () => {
    setGeneralErrorMessage("");

    if (submittedOnce === false) setSubmittedOnce(true);

    const isCnpjValid = validateCnpj(cnpj);
    const isPhoneValid = validatePhoneNumber(phone);

    if (isCnpjValid && isPhoneValid) {
      setButtonsDisabled(true);
      try {
        const storeData: IStore = {
          name: storeNameRef?.current?.value || "",
          email: emailRef?.current?.value || "",
          CNPJ: cnpj,
          phone: phoneRef?.current?.value || "",
          description: descriptionRef?.current?.value || "",
          logradouro: streetRef?.current?.value || "",
          number: numberRef?.current?.value || "",
          district: districtRef?.current?.value || "",
          city: cityRef?.current?.value || "",
          state: stateRef?.current?.value || "",
          CEP: cep,
          country: "Brazil",
          lat: coordinates[0],
          lng: coordinates[1],
          imageDocs: [],
          imageOriginal: [],
          imagePreview: [],
          listTags: "",
          timeMonday,
          timeTuesday,
          timeWednesday,
          timeThursday,
          timeFriday,
          timeSaturday,
          timeSunday,
        };

        const createPayload = {
          store: storeData,
          account: {
            account: {
              bank: bankRef?.current?.value || "",
              issuer: issuerRef?.current?.value || "",
              issuerDigit: issuerDigitRef?.current?.value || "",
              number: accountNumberRef?.current?.value || "",
              bankDigit: bankDigitRef?.current?.value || "",
              accountType: accountType,
            },
            transferType: transferType,
            pix: {
              keyType: pixKeyType,
              key: pixKeyRef?.current?.value || "",
            }
          },
          gatewayId: gatewayIdRef?.current?.value || "",
        };

        const createdStoreResponse = await storeApi.createStore(createPayload);

        if (images.length > 0) {
          const { originalImageUrls, previewImageUrls } =
            await uploadOriginalAndPreviewImagesToFirebase(
              images,
              getStoreImagesFirebasePath(
                createdStoreResponse?.name,
                createdStoreResponse?.id
              )
            );

          const updatedStore = {
            ...storeData,
            imageOriginal: originalImageUrls,
            imagePreview: previewImageUrls
          };

          const updatePayload: UpdateStorePayload = { store: updatedStore };
          await storeApi.updateStore(createdStoreResponse.id, updatePayload);
        }

        window.alert(
            t("storePage.management.add.successMessage", { name: storeData.name })
        );
        navigate("/stores");
      } catch (error: any) {
        setButtonsDisabled(false);
        console.warn("Error adding store: ", error);
        setGeneralErrorMessage(error.response?.data?.message || t("storePage.management.add.unknownError"));
      }
    } else {
      if (!isCnpjValid) cnpjRef.current?.focus();
      else if (!isPhoneValid) phoneRef.current?.focus();
    }
  };

  const renderStoreData = (
    <>
      <ComercialInterfaceHeader title={t("storePage.management.add.title")} />
      <Box sx={sharedStyles.verticalContent}>
        <Box sx={sharedStyles.dividedFormContainer}>
          <TextField
            size="small"
            label={t("storePage.management.add.form.name")}
            inputRef={storeNameRef}
            {...register("storeName")}
            error={!!errors.storeName}
            helperText={errors.storeName?.message}
            fullWidth
          />
          <InputMask
            mask="99.999.999/9999-99"
            disabled={false}
            maskChar="_"
            value={cnpj}
            onChange={(e) => setCnpj(e.target.value)}
          >
          <TextField
            size="small"
            fullWidth
            label={t("storePage.management.add.form.cnpj")}
            inputRef={cnpjRef}
            error={showCustomInputFieldError(!validateCnpj(cnpj), submittedOnce)}
            helperText={
              showCustomInputFieldError(!validateCnpj(cnpj), submittedOnce) &&
                      t("storePage.management.add.invalidCnpj")}
          />
          </InputMask>
        </Box>

        <Box sx={sharedStyles.dividedFormContainer}>
          <InputMask
            mask="(99) 9.9999-9999"
            disabled={false}
            maskChar="_"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          >
            <TextField
            label={t("storePage.management.add.form.phone")}
            size="small"
            fullWidth
            inputRef={phoneRef}
            error={showCustomInputFieldError(!validatePhoneNumber(phone), submittedOnce)}
            helperText={
              showCustomInputFieldError(!validatePhoneNumber(phone), submittedOnce)
                        ? t("storePage.management.add.invalidPhone")
                        : ""
                  }
          />
          </InputMask>
          <TextField
            size="small"
            label={t("storePage.management.add.form.email")}
            placeholder={t("storePage.management.add.form.emailPlaceholder")}
            fullWidth
            inputRef={emailRef}
            {...register("email")}
            error={!!errors.email}
            helperText={errors.email?.message}
          />
        </Box>
        <TextField
          size="small"
          label={t("storePage.management.add.form.description")}
          placeholder={t("storePage.management.add.form.descriptionPlaceholder")}
          fullWidth
          multiline
          rows={5}
          inputRef={descriptionRef}
        />
      </Box>
    </>
  );

  const renderBankingData = (
      <>
        <ComercialInterfaceHeader title="Dados Bancários e Gateway" />
        <Box sx={sharedStyles.verticalContent}>
          <TextField
              size="small"
              label="ID do Gateway"
              inputRef={gatewayIdRef}
              fullWidth
          />

          <Box sx={sharedStyles.dividedFormContainer}>
            <TextField size="small" label="Banco (Código)" inputRef={bankRef} fullWidth />
            <TextField size="small" label="Agência" inputRef={issuerRef} fullWidth />
            <TextField size="small" label="Dígito Agência" inputRef={issuerDigitRef} fullWidth />
          </Box>

          <Box sx={sharedStyles.dividedFormContainer}>
            <TextField size="small" label="Número da Conta" inputRef={accountNumberRef} fullWidth />
            <TextField size="small" label="Dígito Conta" inputRef={bankDigitRef} fullWidth />
            <TextField
                size="small"
                select
                label="Tipo de Conta"
                value={accountType}
                onChange={(e) => setAccountType(e.target.value)}
                SelectProps={{ native: true }}
                fullWidth
                InputLabelProps={{ shrink: true }}
            >
              <option value="CHECKING_ACCOUNT">Conta Corrente</option>
              <option value="SAVINGS_ACCOUNT">Conta Poupança</option>
            </TextField>
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 2, color: 'text.secondary' }}>
            Configuração de Transferência (PIX)
          </Typography>

          <Box sx={sharedStyles.dividedFormContainer}>
            <TextField
                size="small"
                select
                label="Tipo de Chave PIX"
                value={pixKeyType}
                onChange={(e) => setPixKeyType(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: "35%" }}
                InputLabelProps={{ shrink: true }}
            >
              <option value="CPF">CPF</option>
              <option value="CNPJ">CNPJ</option>
              <option value="EMAIL">E-mail</option>
              <option value="PHONE">Telefone</option>
              <option value="RANDOM_KEY">Chave Aleatória</option>
            </TextField>
            <TextField
                size="small"
                label="Chave PIX"
                inputRef={pixKeyRef}
                sx={{ width: "65%" }}
            />
          </Box>
        </Box>
      </>
  );

  const renderAddressData = (
    <>
      <ComercialInterfaceHeader title={t("storePage.management.add.geoTitle")} />

      <Box sx={sharedStyles.verticalContent}>
        <InputMask
          mask="99999-999"
          disabled={false}
          maskChar="_"
          value={cep}
          onChange={(e) => setCep(e.target.value)}
        >
          {<TextField size="small" label={t("storePage.management.add.form.zip")} fullWidth />}
        </InputMask>
        <Box sx={{ ...sharedStyles.verticalContent, flexDirection: "row" }}>
          <TextField
            size="small"
            label={t("storePage.management.add.form.street")}
            inputRef={streetRef}
            sx={{ width: "75%" }}
            InputLabelProps={{ shrink: true }}
            disabled={
              foundedCepAddress !== null && streetRef?.current?.value !== ""
            }
          />
          <TextField
            size="small"
            label={t("storePage.management.add.form.number")}
            inputRef={numberRef}
            sx={{ width: "25%" }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <TextField
          size="small"
          label={t("storePage.management.add.form.district")}
          fullWidth
          inputRef={districtRef}
          InputLabelProps={{ shrink: true }}
          disabled={
            foundedCepAddress !== null && districtRef?.current?.value !== ""
          }
        />
        <Box sx={{ ...sharedStyles.verticalContent, flexDirection: "row" }}>
          <TextField
            size="small"
            label={t("storePage.management.add.form.city")}
            fullWidth
            inputRef={cityRef}
            InputLabelProps={{ shrink: true }}
            disabled={
              foundedCepAddress !== null && cityRef?.current?.value !== ""
            }
          />

          <TextField
            size="small"
            label={t("storePage.management.add.form.state")}
            fullWidth
            inputRef={stateRef}
            InputLabelProps={{ shrink: true }}
            disabled={
              foundedCepAddress !== null && stateRef?.current?.value !== ""
            }
          />
        </Box>

        {foundedCepAddress && (
          <Box sx={{ alignSelf: "flex-end", marginTop: 3 }}>
            <CoordinateSelector
              coordinates={coordinates}
              setCoordinates={setCoordinates}
              brazilianAddress={foundedCepAddress}
            />
          </Box>
        )}
      </Box>
    </>
  );

  const renderImageGalery = (
    <>
      <ImageGalleryHeader
        label={t("storePage.management.add.images.add")}
        icon={<AddPhotoAlternateIcon />}
        setImages={setImages}
      />

      <ImageGalleryContainer>
        <GalleryImageList images={images} setImages={setImages} interable />
      </ImageGalleryContainer>
    </>
  );

  const renderWorkScaleSelector = (
    <>
      <ComercialInterfaceHeader title={t("storePage.management.add.workScaleTitle")} />
      <Box>
        <Typography>
          <Trans i18nKey="storePage.management.add.workScale.defaultText">
            Horário padrão de funcionamento: <b>7:00-11:00</b> e{" "}
            <b>13:00-17:00</b>.
          </Trans>
        </Typography>

        <Typography>{t("storePage.management.add.workScale.customizeText")}</Typography>
      </Box>
      <Box>
        <WorkingScaleSelector
          intervalMinutes={intervalMinutes}
          setIntervalMinutes={setIntervalMinutes}
          timeMonday={timeMonday}
          setTimeMonday={setTimeMonday}
          timeTuesday={timeTuesday}
          setTimeTuesday={setTimeTuesday}
          timeWednesday={timeWednesday}
          setTimeWednesday={setTimeWednesday}
          timeThursday={timeThursday}
          setTimeThursday={setTimeThursday}
          timeFriday={timeFriday}
          setTimeFriday={setTimeFriday}
          timeSaturday={timeSaturday}
          setTimeSaturday={setTimeSaturday}
          timeSunday={timeSunday}
          setTimeSunday={setTimeSunday}
        />
      </Box>
    </>
  );

  const renderAddStorePageContent = (
    <>
      <LeftMarginBackButton />

      <form onSubmit={onSubmit(handleStoreCreation)} style={sharedStyles.form}>
        {renderStoreData}
        {renderAddressData}
        {renderBankingData}
        {renderImageGalery}
        {renderWorkScaleSelector}

        {generalErrorMessage !== "" && (
          <Alert severity="error">{generalErrorMessage}</Alert>
        )}

        <Box sx={sharedStyles.dividedFormContainer}>
          <PrimaryButton
            label={t("storePage.management.add.buttons.confirm")}
            isDisabled={buttonsDisabled}
            isLoading={buttonsDisabled}
            icon={<AddBusinessIcon />}
          />
          <SecondaryButton
            label={t("storePage.management.add.buttons.cancel")}
            onClickAction={() => navigate("/stores")}
            isDisabled={buttonsDisabled}
          />
        </Box>
      </form>
    </>
  );

  return (
    <Box sx={sharedStyles.sharedMainContainer}>
      <ComercialDrawer content={renderAddStorePageContent} />
    </Box>
  );
};

export default AddStore;
